import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import i18n from '../i18n';
import type {
  ApiResponse,
  AuthTokenResponse,
  LoginPayload,
  RegisterPayload,
  LoginResponse,
  MfaVerifyResponse,
  MfaBackupVerifyResponse,
  TotpSetupResponse,
  TotpConfirmResponse,
  MfaSetting,
} from '../types';

// ── Base URL ────────────────────────────────────────────────────────────────
// VITE_API_URL is the host only (e.g. https://api.staging.senihelp24.de).
// The Laravel RouteServiceProvider registers `Route::prefix('v1')` and
// `Route::prefix('v2')`. We append the version per axios instance.
const HOST = (import.meta.env.VITE_API_URL ?? 'https://api.staging.senihelp24.de').replace(/\/+$/, '');

// ── N-MEDIUM-01: CSRF token for Step-Up Cookie protection ──────────────────
// Stored in module-global memory ONLY. Never written to localStorage or
// sessionStorage. Cleared by AuthContext.completeMfa() / cancelMfa().
let _mfaCsrfToken: string | null = null;

/** Set the MFA CSRF token after a successful /auth/login response. */
export function setMfaCsrfToken(token: string): void {
  _mfaCsrfToken = token;
}

/** Clear the MFA CSRF token (call after MFA completion or cancellation). */
export function clearMfaCsrfToken(): void {
  _mfaCsrfToken = null;
}

// ── Shared request/response handling ───────────────────────────────────────

function attachBearerToken(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const token = localStorage.getItem('sh24.token');
  if (token && config.headers) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}

function handle401Redirect(error: unknown): Promise<never> {
  // Type guard: only axios-shaped errors expose .response/.config
  const err = error as { response?: { status?: number }; config?: { url?: string } };
  if (err.response?.status === 401) {
    const url: string = err.config?.url ?? '';
    // Don't redirect on MFA endpoints — invalid codes are handled locally.
    const isMfaEndpoint = url.includes('/auth/mfa/') || url.includes('/me/mfa/');
    if (!isMfaEndpoint) {
      localStorage.removeItem('sh24.token');
      localStorage.removeItem('sh24.user');
      localStorage.setItem('sh24.auth', 'login');
      window.location.href = '/login';
    }
  }
  return Promise.reject(error);
}

const COMMON_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  'Accept-Language': 'de',
} as const;

// ── apiV1 ── legacy resource endpoints (admin CRM data) ────────────────────
// No withCredentials (no MFA cookie needed). No CSRF interceptor.
export const apiV1: AxiosInstance = axios.create({
  baseURL: `${HOST}/v1`,
  headers: { ...COMMON_HEADERS },
  timeout: 15000,
});
apiV1.interceptors.request.use(attachBearerToken);
apiV1.interceptors.response.use(r => r, handle401Redirect);

// ── apiV2 ── new auth + MFA + locale endpoints ─────────────────────────────
// withCredentials = true so the browser sends sh24_mfa_step_up HttpOnly cookie.
export const apiV2: AxiosInstance = axios.create({
  baseURL: `${HOST}/v2`,
  headers: { ...COMMON_HEADERS },
  timeout: 15000,
  withCredentials: true,
});
apiV2.interceptors.request.use(attachBearerToken);

// MFA CSRF header — only attached on MFA-CSRF-protected endpoints (N-MEDIUM-01).
apiV2.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!_mfaCsrfToken) return config;

  const url = config.url ?? '';
  const method = (config.method ?? '').toUpperCase();

  const isMfaChallenge = url.includes('/auth/mfa/');
  const isMfaManagement =
    url.includes('/me/mfa/') && (method === 'PATCH' || method === 'DELETE');

  if ((isMfaChallenge || isMfaManagement) && config.headers) {
    config.headers['X-MFA-CSRF-Token'] = _mfaCsrfToken;
  }
  return config;
});

apiV2.interceptors.response.use(r => r, handle401Redirect);

// ── Auth endpoints (v2) ────────────────────────────────────────────────────

export const authApi = {
  /** Step 1: credentials → may return MFA challenge or direct token.
   *  N-MEDIUM-01: if the response contains csrf_token, store it in memory
   *  for use in subsequent X-MFA-CSRF-Token headers. */
  login: async (payload: LoginPayload): Promise<LoginResponse> => {
    const response = await apiV2.post<{ data: LoginResponse & { csrf_token?: string } }>('/auth/login', payload);
    const data = response.data.data;
    if (data.csrf_token) {
      setMfaCsrfToken(data.csrf_token);
    }
    return data;
  },

  logout: async (): Promise<void> => {
    try {
      await apiV2.post('/auth/logout');
    } finally {
      localStorage.removeItem('sh24.token');
      localStorage.removeItem('sh24.user');
    }
  },

  register: async (payload: RegisterPayload): Promise<ApiResponse<AuthTokenResponse>> => {
    const response = await apiV2.post<ApiResponse<AuthTokenResponse>>('/auth/register', payload);
    return response.data;
  },

  me: async () => {
    const response = await apiV2.get('/auth/me');
    return response.data;
  },
};

// ── MFA Challenge endpoints (step-up-cookie is sent automatically) ──────────

export const mfaApi = {
  /**
   * POST /auth/mfa/challenge/send
   * Sends an email/SMS OTP.  Not called for TOTP (no send step needed).
   */
  sendChallenge: async (userIdHint: string, method: 'email' | 'sms') => {
    const response = await apiV2.post<{ data: { challenge_id: string; method: string; expires_in: number } }>(
      '/auth/mfa/challenge/send',
      { user_id_hint: userIdHint, method }
    );
    return response.data.data;
  },

  /**
   * POST /auth/mfa/challenge/verify
   * Verifies a 6-digit code (works for email, sms, and totp).
   */
  verifyChallenge: async (params: {
    challenge_id: string | null;
    code: string;
    user_id_hint: string;
  }): Promise<MfaVerifyResponse> => {
    const response = await apiV2.post<{ data: MfaVerifyResponse }>(
      '/auth/mfa/challenge/verify',
      params
    );
    return response.data.data;
  },

  /**
   * POST /auth/mfa/backup/verify
   * Verifies a backup recovery code.
   */
  verifyBackupCode: async (userIdHint: string, code: string): Promise<MfaBackupVerifyResponse> => {
    const response = await apiV2.post<{ data: MfaBackupVerifyResponse }>(
      '/auth/mfa/backup/verify',
      { user_id_hint: userIdHint, code }
    );
    return response.data.data;
  },
};

// ── MFA Setup endpoints (authenticated) ────────────────────────────────────

/**
 * Backend has two parallel /me/mfa route groups: one behind `auth:user` and
 * one behind `auth:admin` (under /admin/ prefix). The token issued at login
 * is scoped to exactly one guard, so the frontend has to pick the right
 * prefix based on the logged-in role. We read it from the same localStorage
 * blob that AuthContext writes.
 */
export function meScope(): '' | '/admin' {
  try {
    const raw = localStorage.getItem('sh24.user');
    if (!raw) return '';
    const u = JSON.parse(raw) as { role?: string };
    // Defensive: bis Mai 2026 lieferten /v2/auth/mfa/challenge/verify und
    // /v2/auth/mfa/backup/verify die User-Payload OHNE `role` zurueck — alte
    // Sessions in localStorage haben deshalb `role === undefined`, was uns
    // sonst auf /v2/me/* lenkt und mit einem Admin-Token 403 produziert.
    // Mangels User-Login in CRM 2.0 sind Admin-Sessions der einzige Real-Case
    // → wenn role fehlt, /admin annehmen. Sobald alle Tokens ablaufen kann
    // dieser Fallback wieder restriktiver werden.
    if (!u.role) return '/admin';
    return u.role === 'admin' ? '/admin' : '';
  } catch {
    return '';
  }
}

export const mfaSetupApi = {
  /**
   * POST /me/mfa/setup/email
   * Activates email as a 2FA method (idempotent). The admin's email is
   * already verified at registration, so no extra verify round-trip is needed.
   */
  setupEmail: async (): Promise<{ method: 'email'; is_primary: boolean; enabled_at: string }> => {
    const response = await apiV2.post<{ data: { method: 'email'; is_primary: boolean; enabled_at: string } }>(
      `${meScope()}/me/mfa/setup/email`,
    );
    return response.data.data;
  },

  /**
   * POST /me/mfa/setup/totp  (or /admin/me/mfa/setup/totp for admin sessions)
   * Generates TOTP secret + QR SVG.
   */
  setupTotp: async (): Promise<TotpSetupResponse> => {
    const response = await apiV2.post<{ data: TotpSetupResponse }>(`${meScope()}/me/mfa/setup/totp`);
    return response.data.data;
  },

  /**
   * POST /me/mfa/setup/totp/confirm
   * Confirms the first TOTP code to activate the method.
   */
  confirmTotp: async (code: string): Promise<TotpConfirmResponse> => {
    const response = await apiV2.post<{ data: TotpConfirmResponse }>(
      `${meScope()}/me/mfa/setup/totp/confirm`,
      { code }
    );
    return response.data.data;
  },

  /**
   * POST /me/mfa/setup/sms
   * Registers a phone number and sends a verify code.
   */
  setupSms: async (mobile: string) => {
    const response = await apiV2.post<{ data: { challenge_id: string; expires_in: number } }>(
      `${meScope()}/me/mfa/setup/sms`,
      { mobile }
    );
    return response.data.data;
  },

  /**
   * POST /me/mfa/setup/sms/confirm
   * Confirms the SMS verify code to activate SMS method.
   */
  confirmSms: async (challengeId: string, code: string) => {
    const response = await apiV2.post<{ data: { confirmed: boolean } }>(
      `${meScope()}/me/mfa/setup/sms/confirm`,
      { challenge_id: challengeId, code }
    );
    return response.data.data;
  },

  /**
   * GET /me/mfa/methods
   * Returns active MFA methods for the authenticated user.
   */
  getMethods: async (): Promise<MfaSetting[]> => {
    const response = await apiV2.get<{ data: MfaSetting[] }>(`${meScope()}/me/mfa/methods`);
    return response.data.data;
  },

  /**
   * PATCH /me/mfa/primary  { method }
   * Sets the primary MFA method (the one auto-selected at next login).
   */
  setPrimary: async (method: 'email' | 'sms' | 'totp'): Promise<{ primary: string }> => {
    const response = await apiV2.patch<{ data: { primary: string } }>(
      `${meScope()}/me/mfa/primary`,
      { method },
    );
    return response.data.data;
  },

  /**
   * DELETE /me/mfa/method/{method}  — requires fresh Step-Up cookie + CSRF.
   * Will 401 if the user only has a regular Passport token (no recent step-up).
   * The frontend should prompt for credentials and re-call /v2/auth/login
   * before retrying this.
   */
  deleteMethod: async (method: 'email' | 'sms' | 'totp'): Promise<void> => {
    await apiV2.delete(`${meScope()}/me/mfa/method/${method}`);
  },

  /**
   * POST /me/mfa/backup-codes/generate  — requires fresh Step-Up cookie + CSRF.
   * Returns 10 plain backup codes; shown to the user ONCE.
   */
  generateBackupCodes: async (): Promise<{ backup_codes: string[] }> => {
    const response = await apiV2.post<{ data: { backup_codes: string[] } }>(
      `${meScope()}/me/mfa/backup-codes/generate`,
    );
    return response.data.data;
  },
};

// ── Dashboard Config (v2, admin-scoped) ────────────────────────────────────

export interface DashboardWidgetSettings {
  visible: boolean;
  mode: 'full' | 'summary';
}

export interface DashboardLayoutItem {
  i: string;  // widget id — matches react-grid-layout key
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DashboardConfig {
  layout?: DashboardLayoutItem[];
  widgets?: Record<string, DashboardWidgetSettings>;
}

export const dashboardConfigApi = {
  /** GET /api/v2/admin/me/dashboard-config */
  get: async (): Promise<DashboardConfig> => {
    const res = await apiV2.get<{ data: { config: DashboardConfig } }>('/admin/me/dashboard-config');
    return res.data.data.config ?? {};
  },

  /** PUT /api/v2/admin/me/dashboard-config */
  save: async (config: DashboardConfig): Promise<DashboardConfig> => {
    const res = await apiV2.put<{ data: { config: DashboardConfig } }>(
      '/admin/me/dashboard-config',
      { config },
    );
    return res.data.data.config;
  },
};

// ── Dashboards je Bereich (Paket 2310e) ───────────────────────────────────────
//
// Auflösung des aktiven Layouts: User-Layout > Bereichs-Standard > eingebauter
// Default. Der Bereichs-Standard gilt für alle Admins, die in diesem Bereich
// noch kein eigenes Layout gespeichert haben, und wird mit dem Recht
// `menu.einstellungen` gesetzt. Bereiche: start (Startseite), pb (PB-Profil),
// bk (BK-Profil), anfragen.
export type DashboardArea = 'start' | 'pb' | 'bk' | 'anfragen';
export type DashboardSource = 'user' | 'default' | 'builtin';
export const DASHBOARD_DEFAULTS_PERMISSION = 'menu.einstellungen';

export const dashboardAreaApi = {
  /** GET /admin/me/dashboard-config?area= : Blob inkl. aufgelöstem Bereichs-Standard. */
  get: async (area: DashboardArea): Promise<{ config: DashboardConfig & Record<string, unknown>; source: DashboardSource }> => {
    const res = await apiV2.get<{ data: { config: DashboardConfig & Record<string, unknown> }; meta?: { source?: DashboardSource } }>(
      '/admin/me/dashboard-config', { params: { area } },
    );
    return { config: res.data.data.config ?? {}, source: res.data.meta?.source ?? 'builtin' };
  },
  /** DELETE /admin/me/dashboard-config/{area} : eigenes Layout verwerfen. */
  reset: async (area: DashboardArea): Promise<{ config: DashboardConfig & Record<string, unknown>; source: DashboardSource }> => {
    const res = await apiV2.delete<{ data: { config: DashboardConfig & Record<string, unknown> }; meta?: { source?: DashboardSource } }>(
      `/admin/me/dashboard-config/${area}`,
    );
    return { config: res.data.data.config ?? {}, source: res.data.meta?.source ?? 'builtin' };
  },
  /** GET /admin/dashboard-defaults/{area} : Bereichs-Standard (null = keiner gesetzt). */
  getDefault: async (area: DashboardArea): Promise<Record<string, unknown> | null> => {
    const res = await apiV2.get<{ data: { config: Record<string, unknown> | null } }>(`/admin/dashboard-defaults/${area}`);
    return res.data.data.config ?? null;
  },
  /** PUT /admin/dashboard-defaults/{area} (Recht menu.einstellungen). */
  setDefault: async (area: DashboardArea, config: Record<string, unknown>): Promise<void> => {
    await apiV2.put(`/admin/dashboard-defaults/${area}`, { config });
  },
  /** DELETE /admin/dashboard-defaults/{area} (Recht menu.einstellungen). */
  clearDefault: async (area: DashboardArea): Promise<void> => {
    await apiV2.delete(`/admin/dashboard-defaults/${area}`);
  },
};

// ── PB Dashboard Config (stored inside the same dashboard-config blob) ────────

/**
 * Per-widget settings for the PB Dashboard (stored under the `pb_dashboard`
 * key inside the admin's dashboard-config JSON blob).
 *
 *   visible: boolean — show/hide the widget on the PB dashboard
 *   mode: 'compact' | 'expanded'
 *     compact  = small summary card (preview only)
 *     expanded = the full interactive Tab component embedded inline
 */
export interface PbDashboardWidgetSettings {
  visible: boolean;
  mode: 'compact' | 'expanded';
}

export type PbDashboardWidgetId =
  | 'betreuungen'
  | 'aufgaben'
  | 'notizen'
  | 'nachrichten'
  | 'dokumente'
  | 'medien'
  | 'bewertungen'
  | 'karte'
  | 'matching'
  | 'gespraechsprotokolle';

/**
 * Order/position item for PB Dashboard widgets.
 * `order` stores the widget order as an array of PbDashboardWidgetId.
 * `dragEnabled` tracks whether the drag toggle is currently on.
 */
export interface PbDashboardMeta {
  order?: PbDashboardWidgetId[];
  dragEnabled?: boolean;
}

export type PbDashboardConfig = Partial<Record<PbDashboardWidgetId, PbDashboardWidgetSettings>> & {
  _meta?: PbDashboardMeta;
};

export const DEFAULT_PB_WIDGET_SETTINGS: PbDashboardWidgetSettings = {
  visible: true,
  mode: 'compact',
};

/**
 * Read and write the `pb_dashboard` sub-key inside the existing
 * /v2/admin/me/dashboard-config endpoint. We merge with the existing
 * blob so the global dashboard layout is never overwritten.
 */
export const pbDashboardConfigApi = {
  get: async (): Promise<PbDashboardConfig> => {
    const { config } = await dashboardAreaApi.get('pb');
    return (config.pb_dashboard as PbDashboardConfig | undefined) ?? {};
  },

  /** Wie get(), zusätzlich mit Quelle des Layouts (user | default | builtin). */
  getWithSource: async (): Promise<{ config: PbDashboardConfig; source: DashboardSource }> => {
    const { config, source } = await dashboardAreaApi.get('pb');
    return { config: (config.pb_dashboard as PbDashboardConfig | undefined) ?? {}, source };
  },

  save: async (pbConfig: PbDashboardConfig): Promise<PbDashboardConfig> => {
    // Fetch current full config first so we don't overwrite global dashboard settings.
    const fullRes = await apiV2.get<{ data: { config: DashboardConfig & { pb_dashboard?: PbDashboardConfig } } }>(
      '/admin/me/dashboard-config',
    );
    const current = fullRes.data.data.config ?? {};
    const merged = { ...current, pb_dashboard: pbConfig };
    const putRes = await apiV2.put<{ data: { config: typeof merged } }>(
      '/admin/me/dashboard-config',
      { config: merged },
    );
    return (putRes.data.data.config as typeof merged).pb_dashboard ?? {};
  },
};

// ── PB Stundenplan / Weekly schedule (US-005 / TASK-BE-11) ──────────────────

/**
 * A single time block in a PB's weekly schedule.
 *
 *   weekday    — 1 = Monday … 7 = Sunday
 *   start_time — "HH:MM"
 *   end_time   — "HH:MM" (must be > start_time)
 *   care_type  — selected care type value (from options.care_types) or null
 *   note       — optional free-text note or null
 *
 * `id` is present on blocks returned by the backend and omitted on
 * client-side draft blocks that have not been persisted yet.
 */
export interface PbScheduleBlock {
  id?: number;
  weekday: number;
  start_time: string;
  end_time: string;
  care_type: string | null;
  note: string | null;
}

export interface PbScheduleCareTypeOption {
  value: string;
  label: string;
}

export interface PbScheduleResponse {
  user_id: number;
  blocks: PbScheduleBlock[];
  options: {
    care_types: PbScheduleCareTypeOption[];
  };
}

/** GET /v2/admin/users/{id}/schedule → schedule blocks + care_type options. */
export async function getPbSchedule(userId: number): Promise<PbScheduleResponse> {
  const res = await apiV2.get<{ data: PbScheduleResponse }>(`/admin/users/${userId}/schedule`);
  return res.data.data;
}

/**
 * PUT /v2/admin/users/{id}/schedule — full-replace of all blocks.
 *
 * Sends only the persisted-relevant fields (the backend re-keys everything),
 * and returns the same shape as `getPbSchedule`. On overlapping blocks the
 * backend responds 422 with `errors:[{code:'E_SCHEDULE_OVERLAP'}]`.
 */
export async function putPbSchedule(
  userId: number,
  blocks: PbScheduleBlock[],
): Promise<PbScheduleResponse> {
  const payload = {
    blocks: blocks.map(b => ({
      weekday: b.weekday,
      start_time: b.start_time,
      end_time: b.end_time,
      care_type: b.care_type,
      note: b.note,
    })),
  };
  const res = await apiV2.put<{ data: PbScheduleResponse }>(
    `/admin/users/${userId}/schedule`,
    payload,
  );
  return res.data.data;
}

// ── US-005 PB Zeiterfassung (Stundenzettel: datierte Ist-Zeiten) ───────────
//
// Im Gegensatz zum wiederkehrenden Wochen-Stundenplan (PbSchedule) sind dies
// datierte Termine. Endpoints unter /v2/admin/users/{id}/time-entries.

/** Ein serialisierter Zeiteintrag, wie ihn der Server zurückgibt. */
export interface PbTimeEntry {
  id: number;
  /** ISO-Datum "YYYY-MM-DD". */
  date: string;
  start_time: string;          // "HH:MM"
  end_time: string;            // "HH:MM"
  care_type: string | null;
  note: string | null;
  bk_user_id: number | null;
  bk_name: string | null;
  /** Dauer in Minuten (Server-berechnet). */
  minutes: number;
}

/** Eingabe für Anlegen/Ändern eines Eintrags (Feldnamen = Backend-Validierung). */
export interface PbTimeEntryInput {
  entry_date: string;          // "YYYY-MM-DD"
  start_time: string;          // "HH:MM"
  end_time: string;            // "HH:MM"
  care_type: string | null;
  note: string | null;
  bk_user_id: number | null;
}

export interface PbTimeEntriesResponse {
  user_id: number;
  from: string;
  to: string;
  entries: PbTimeEntry[];
  summary: { count: number; total_minutes: number; total_hours: number };
  options: { care_types: PbScheduleCareTypeOption[] };
}

/** GET /v2/admin/users/{id}/time-entries?from=&to= — Einträge im Zeitraum. */
export async function listPbTimeEntries(
  userId: number,
  range?: { from?: string; to?: string },
): Promise<PbTimeEntriesResponse> {
  const res = await apiV2.get<{ data: PbTimeEntriesResponse }>(
    `/admin/users/${userId}/time-entries`,
    { params: { from: range?.from, to: range?.to } },
  );
  return res.data.data;
}

/** POST /v2/admin/users/{id}/time-entries — neuen Termin anlegen. */
export async function createPbTimeEntry(userId: number, input: PbTimeEntryInput): Promise<PbTimeEntry> {
  const res = await apiV2.post<{ data: PbTimeEntry }>(`/admin/users/${userId}/time-entries`, input);
  return res.data.data;
}

/** PUT /v2/admin/users/{id}/time-entries/{entry} — Termin ändern. */
export async function updatePbTimeEntry(userId: number, entryId: number, input: PbTimeEntryInput): Promise<PbTimeEntry> {
  const res = await apiV2.put<{ data: PbTimeEntry }>(`/admin/users/${userId}/time-entries/${entryId}`, input);
  return res.data.data;
}

/** DELETE /v2/admin/users/{id}/time-entries/{entry} — Termin löschen. */
export async function deletePbTimeEntry(userId: number, entryId: number): Promise<void> {
  await apiV2.delete(`/admin/users/${userId}/time-entries/${entryId}`);
}

/**
 * GET /v2/admin/users/{id}/time-entries/export — Protokoll als XLSX/PDF.
 * Lädt die Datei als Blob (Bearer-Token nötig) und stößt im Browser den
 * Download an. Gibt den verwendeten Dateinamen zurück.
 */
export async function exportPbTimeEntries(
  userId: number,
  params: { from: string; to: string; format: 'xlsx' | 'pdf' },
): Promise<string> {
  const res = await apiV2.get(`/admin/users/${userId}/time-entries/export`, {
    params,
    responseType: 'blob',
  });

  // Dateiname aus Content-Disposition, sonst Fallback bauen.
  const disposition = (res.headers?.['content-disposition'] ?? '') as string;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match
    ? decodeURIComponent(match[1])
    : `zeiterfassung_${userId}_${params.from}_${params.to}.${params.format}`;

  const blob = res.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return filename;
}

// ── US-006 — Öffentlicher Stundenplan-Link mit PIN ─────────────────────────
//
// Admin verwaltet den Link im PB-Profil; der Kunde ruft `/stundenplan/{token}`
// ohne Account auf und füllt den Plan mit PIN aus.

/** Admin-Sicht auf den Kundenlink eines PB (inkl. Klartext-PIN). */
export interface PbScheduleLinkInfo {
  token: string;
  pin: string;
  url: string;
  locked: boolean;
  locked_at: string | null;
  last_accessed_at: string | null;
  throttled: boolean;
  created_at: string | null;
}

/** Öffentliche Plan-Antwort: Stundenplan + PB-Name + Sperr-Status. */
export interface PublicSchedulePayload extends PbScheduleResponse {
  pb_name: string | null;
  locked: boolean;
  locked_at: string | null;
}

/**
 * Eigene Axios-Instanz für die öffentlichen Endpoints: KEIN Bearer-Token,
 * KEINE 401-Redirect-Logik (ein 401 = falscher PIN, nicht „ausgeloggt").
 */
const apiPublic: AxiosInstance = axios.create({
  baseURL: `${HOST}/v2`,
  headers: { ...COMMON_HEADERS },
  timeout: 15000,
});

/** POST /v2/public/schedule/{token}/open — PIN prüfen, Plan laden. */
export async function openPublicSchedule(token: string, pin: string): Promise<PublicSchedulePayload> {
  const res = await apiPublic.post<{ data: PublicSchedulePayload }>(
    `/public/schedule/${encodeURIComponent(token)}/open`,
    { pin },
  );
  return res.data.data;
}

/**
 * POST /v2/public/schedule/{token}/open — Admin-Bypass (kein PIN).
 * Sendet den Bearer-Token aus localStorage via apiV2 (mit Auth-Header).
 * Der Backend-Endpunkt akzeptiert einen gültigen admin-Bearer und überspringt
 * die PIN-Prüfung. Wird nur aufgerufen wenn ein Admin eingeloggt ist.
 */
export async function openPublicScheduleAsAdmin(token: string): Promise<PublicSchedulePayload> {
  const res = await apiV2.post<{ data: PublicSchedulePayload }>(
    `/public/schedule/${encodeURIComponent(token)}/open`,
    {},
  );
  return res.data.data;
}

/**
 * POST /v2/public/schedule/{token}/save — Zwischenspeichern (Autosave), OHNE
 * den Plan zu sperren. Wird nach jedem Eintrag aufgerufen.
 */
export async function savePublicSchedule(
  token: string,
  pin: string,
  blocks: PbScheduleBlock[],
  opts: { asAdmin?: boolean } = {},
): Promise<PublicSchedulePayload> {
  const payload = {
    pin,
    blocks: blocks.map(b => ({
      weekday: b.weekday,
      start_time: b.start_time,
      end_time: b.end_time,
      care_type: b.care_type,
      note: b.note,
    })),
  };
  // Admin-Bypass: ohne PIN, dafür mit Bearer (apiV2). Über apiPublic ginge
  // ein leerer PIN raus → 422 und ein Fehlversuch im PIN-Rate-Limit des Kunden.
  const client = opts.asAdmin ? apiV2 : apiPublic;
  const res = await client.post<{ data: PublicSchedulePayload }>(
    `/public/schedule/${encodeURIComponent(token)}/save`,
    opts.asAdmin ? { blocks: payload.blocks } : payload,
  );
  return res.data.data;
}

/** POST /v2/public/schedule/{token}/submit — Plan speichern und festlegen. */
export async function submitPublicSchedule(
  token: string,
  pin: string,
  blocks: PbScheduleBlock[],
  opts: { asAdmin?: boolean } = {},
): Promise<PublicSchedulePayload> {
  const payload = {
    pin,
    blocks: blocks.map(b => ({
      weekday: b.weekday,
      start_time: b.start_time,
      end_time: b.end_time,
      care_type: b.care_type,
      note: b.note,
    })),
  };
  // Admin-Bypass: ohne PIN, dafür mit Bearer (apiV2). Über apiPublic ginge
  // ein leerer PIN raus → 422 und ein Fehlversuch im PIN-Rate-Limit des Kunden.
  const client = opts.asAdmin ? apiV2 : apiPublic;
  const res = await client.post<{ data: PublicSchedulePayload }>(
    `/public/schedule/${encodeURIComponent(token)}/submit`,
    opts.asAdmin ? { blocks: payload.blocks } : payload,
  );
  return res.data.data;
}

/** GET /v2/admin/users/{id}/schedule-link — Link-Status (oder null). */
export async function getPbScheduleLink(userId: number): Promise<PbScheduleLinkInfo | null> {
  const res = await apiV2.get<{ data: PbScheduleLinkInfo | null }>(`/admin/users/${userId}/schedule-link`);
  return res.data.data;
}

/** POST /v2/admin/users/{id}/schedule-link — Link erzeugen / neu erzeugen. */
export async function createPbScheduleLink(userId: number): Promise<PbScheduleLinkInfo> {
  const res = await apiV2.post<{ data: PbScheduleLinkInfo }>(`/admin/users/${userId}/schedule-link`, {});
  return res.data.data;
}

/** DELETE /v2/admin/users/{id}/schedule-link — Link widerrufen. */
export async function deletePbScheduleLink(userId: number): Promise<void> {
  await apiV2.delete(`/admin/users/${userId}/schedule-link`);
}

/** POST /v2/admin/users/{id}/schedule-link/unlock — festgelegten Plan wieder freigeben. */
export async function unlockPbSchedule(userId: number): Promise<PbScheduleLinkInfo> {
  const res = await apiV2.post<{ data: PbScheduleLinkInfo }>(`/admin/users/${userId}/schedule-link/unlock`, {});
  return res.data.data;
}

/**
 * POST /v2/admin/users/{id}/schedule-link/send — Link + PIN per E-Mail an einen Ansprechpartner.
 * `url` ist die im Browser gebaute, korrekte öffentliche URL (Origin + Token);
 * das Backend verwendet sie für die Mail, statt sich auf WEBAPP_URL zu verlassen.
 */
export async function sendPbScheduleLink(
  userId: number,
  recipient: { email: string; name?: string | null; url?: string },
): Promise<{ sent_to: string }> {
  const res = await apiV2.post<{ data: { sent_to: string } }>(
    `/admin/users/${userId}/schedule-link/send`,
    recipient,
  );
  return res.data.data;
}

// ── Setcard PDF (CRM 2.0) ──────────────────────────────────────────────────
//
// GET /v2/admin/pb/{id}/setcard — rendert eine PB-Setcard (CRM-2.0-Design) als
// PDF. Lädt die Datei als Blob (Bearer-Token nötig) und stößt im Browser den
// Download an. Gibt den verwendeten Dateinamen zurück.

export async function downloadPbSetcard(userId: number): Promise<string> {
  const res = await apiV2.get(`/admin/pb/${userId}/setcard`, {
    responseType: 'blob',
    timeout: 30000,
  });

  const disposition = (res.headers?.['content-disposition'] ?? '') as string;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : `SH24_Setcard-${userId}.pdf`;

  const blob = res.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return filename;
}

// ── US-PB-SETCARD — Setcard Public Share Link ──────────────────────────────
//
// Admin verwaltet den Link im PB-Profil-Sidebar; externe Nutzer können die
// Setcard-PDF ohne Login über /setcard/{token} + PIN öffnen.

/**
 * JSON-Datenstruktur, die beide Setcard-Endpunkte zurückgeben:
 * - GET /v2/admin/pb/{id}/setcard-data (Admin)
 * - POST /v2/public/setcard/{token}/open (Public)
 */
export interface SetcardData {
  id: number;
  vorname: string;
  nachname: string;
  anrede: string;
  alter: number;
  geschlecht: string;
  groesse: number | null;
  gewicht: number | null;
  pflegegrad: string | null;
  nettolohn: string | null;
  familienstand: string | null;
  beweglichkeit: string | null;
  einschraenkungen: string | null;
  sonstige_einschraenkungen: string | null;
  besserung: string | null;
  sinneswahrnehmung: string | null;
  geisteszustand: string | null;
  schlafverhalten: string | null;
  grundpflege: string | null;
  hygiene: string | null;
  urinkontrolle: string | null;
  stuhlkontrolle: string | null;
  ankleiden: string | null;
  essen: string | null;
  charaktereigenschaften: string | null;
  bk_anrede: string | null;
  wohnsituation: string | null;
  wohnzustand: string | null;
  wohnort: string | null;
  haustiere: string | null;
  haustiere_welche: string | null;
  einkaufsmoeglichkeiten: string | null;
  bk_wohnen: string | null;
  bk_aktivitaeten: string | null;
  bk_deutsch: string | null;
  bk_fuehrerschein: string | null;
  hobbys: string | null;
  umfeld: string | null;
  sonstiges: string | null;
  photo_base64: string | null;
  ratings: Array<{
    name: string;
    rating_gesamt: number;
    rating_text: string;
    date: string;
  }>;
}

/** Admin-Sicht auf den Setcard-Link eines PB. */
export interface PbSetcardLinkInfo {
  token: string;
  pin: string;
  last_accessed_at: string | null;
  created_at: string | null;
}

/** GET /v2/admin/users/{id}/setcard-link — Link-Status (oder null). */
export async function getPbSetcardLink(userId: number | string): Promise<PbSetcardLinkInfo | null> {
  const res = await apiV2.get<{ data: PbSetcardLinkInfo | null }>(`/admin/users/${userId}/setcard-link`);
  return res.data.data;
}

/** POST /v2/admin/users/{id}/setcard-link — Link erzeugen / neu erzeugen. */
export async function createPbSetcardLink(userId: number | string): Promise<PbSetcardLinkInfo> {
  const res = await apiV2.post<{ data: PbSetcardLinkInfo }>(`/admin/users/${userId}/setcard-link`, {});
  return res.data.data;
}

/** DELETE /v2/admin/users/{id}/setcard-link — Link widerrufen. */
export async function deletePbSetcardLink(userId: number | string): Promise<void> {
  await apiV2.delete(`/admin/users/${userId}/setcard-link`);
}

/**
 * POST /v2/public/setcard/{token}/open — PIN prüfen und Setcard-Daten laden.
 * Nutzt eine eigene Axios-Instanz ohne Bearer-Token (öffentlicher Endpunkt).
 * Gibt SetcardData als JSON zurück.
 */
export async function openPublicSetcard(token: string, pin: string): Promise<SetcardData> {
  const res = await apiPublic.post<{ data: SetcardData }>(
    `/public/setcard/${encodeURIComponent(token)}/open`,
    { pin },
  );
  return res.data.data;
}

/**
 * POST /v2/public/setcard/{token}/open — Admin-Bypass (kein PIN).
 * Sendet den Bearer-Token aus localStorage via apiV2 (mit Auth-Header).
 * Der Backend-Endpunkt akzeptiert einen gültigen admin-Bearer und überspringt
 * die PIN-Prüfung. Wird nur aufgerufen wenn ein Admin eingeloggt ist.
 */
export async function openPublicSetcardAsAdmin(token: string): Promise<SetcardData> {
  const res = await apiV2.post<{ data: SetcardData }>(
    `/public/setcard/${encodeURIComponent(token)}/open`,
    {},
  );
  return res.data.data;
}

/**
 * POST /v2/public/setcard/{token}/pdf — PIN prüfen und Setcard-PDF laden.
 * Nutzt eine eigene Axios-Instanz ohne Bearer-Token (öffentlicher Endpunkt).
 * Gibt einen Blob zurück (application/pdf).
 */
export async function openPublicSetcardPdf(token: string, pin: string): Promise<Blob> {
  const res = await apiPublic.post(
    `/public/setcard/${encodeURIComponent(token)}/pdf`,
    { pin },
    { responseType: 'blob' },
  );
  return res.data as Blob;
}

/**
 * POST /v2/public/setcard/{token}/pdf — Admin-Bypass (kein PIN).
 * Gegenstück zu openPublicSetcardAsAdmin für den PDF-Export: nutzt apiV2
 * (Bearer-Token aus localStorage), damit PublicSetcardController::pdf() den
 * PIN-Check überspringt (Auth::guard('admin')->check()). Ohne diese Variante
 * rief die Seite im Admin-Bypass-Pfad weiterhin openPublicSetcardPdf() mit
 * leerem PIN über apiPublic (kein Bearer) auf — das schlug immer mit
 * E_INVALID_PIN fehl (Bug B1, Sammelpaket 22.09.2026).
 */
export async function openPublicSetcardPdfAsAdmin(token: string): Promise<Blob> {
  const res = await apiV2.post(
    `/public/setcard/${encodeURIComponent(token)}/pdf`,
    {},
    { responseType: 'blob' },
  );
  return res.data as Blob;
}

/**
 * GET /v2/admin/pb/{id}/setcard-data — Setcard-Daten für Admin-Ansicht.
 * Gibt SetcardData als JSON zurück (Bearer-Token nötig).
 */
export async function getAdminSetcardData(userId: number): Promise<SetcardData> {
  const res = await apiV2.get<{ data: SetcardData }>(`/admin/pb/${userId}/setcard-data`);
  return res.data.data;
}

// ── E-Mail-Vorlagen (CRM 2.0, admin-scoped v2) ─────────────────────────────
//
// Backend: app/Http/Controllers/v2/Admin/EmailTemplateController.php.
// ACHTUNG: Anders als die übrigen v2-Endpunkte nutzt dieser Controller das
// v1-artige Envelope ({ type:'success', templates / template / variables /
// html / result }) — NICHT das { data: … }-Envelope. Wir lesen darum direkt
// res.data.<key> statt res.data.data.

export interface EmailTemplate {
  id: number;
  name: string;
  subject: string | null;
  body: string | null;
  /** AdminUpload-IDs (siehe EmailTemplate::$casts['attachments'='array']). */
  attachments: number[] | null;
  /** Batch-12b: "profiltyp:<Rolle>" | "kreis:<key>" | null. */
  default_empfaenger?: string | null;
  created_by?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/** Einfügbare Variable für den Body — aus EmailTemplate::AVAILABLE_VARIABLES. */
export interface EmailTemplateVariable {
  token: string;   // z.B. "{{vorname}}"
  label: string;   // z.B. "Vorname"
}

export interface EmailTemplateInput {
  name: string;
  subject?: string | null;
  body?: string | null;
  attachments?: number[];
  /** Batch-12b: "profiltyp:<Rolle>" | "kreis:<key>" | null. */
  default_empfaenger?: string | null;
}

/** Versand-Ergebnis (BrevoMailer::send). */
export interface EmailTemplateSendResult {
  ok: boolean;
  message: string;
  message_id?: string;
  status?: number;
}

/** Ergebnis eines Admin-Datei-Uploads (AdminUpload-Datensatz). */
export interface AdminUploadResult {
  id: number;
  name: string;
  datei: string;
  typ?: string;
  groesse?: number;
  uri?: string;
}

export const emailTemplatesApi = {
  /** GET /v2/admin/email-templates → alle Vorlagen (nach Name sortiert). */
  list: async (): Promise<EmailTemplate[]> => {
    const res = await apiV2.get<{ type: string; templates: EmailTemplate[] }>('/admin/email-templates');
    return res.data.templates ?? [];
  },

  /** GET /v2/admin/email-templates/{id} → einzelne Vorlage. */
  get: async (id: number): Promise<EmailTemplate> => {
    const res = await apiV2.get<{ type: string; template: EmailTemplate }>(`/admin/email-templates/${id}`);
    return res.data.template;
  },

  /** POST /v2/admin/email-templates → neue Vorlage. */
  create: async (input: EmailTemplateInput): Promise<EmailTemplate> => {
    const res = await apiV2.post<{ type: string; template: EmailTemplate }>('/admin/email-templates', input);
    return res.data.template;
  },

  /** PUT /v2/admin/email-templates/{id} → Vorlage aktualisieren. */
  update: async (id: number, input: EmailTemplateInput): Promise<EmailTemplate> => {
    const res = await apiV2.put<{ type: string; template: EmailTemplate }>(`/admin/email-templates/${id}`, input);
    return res.data.template;
  },

  /** DELETE /v2/admin/email-templates/{id}. */
  remove: async (id: number): Promise<void> => {
    await apiV2.delete(`/admin/email-templates/${id}`);
  },

  /** GET /v2/admin/email-templates/variables → einfügbare {{token}}-Variablen. */
  getVariables: async (): Promise<EmailTemplateVariable[]> => {
    const res = await apiV2.get<{ type: string; variables: EmailTemplateVariable[] }>(
      '/admin/email-templates/variables',
    );
    return res.data.variables ?? [];
  },

  /**
   * POST /v2/admin/email-templates/preview → fertiges HTML (Body im Standard-
   * Design, Variablen durch Beispielwerte ersetzt). Eigene Variablen können
   * optional mitgegeben werden; sonst füllt der Server Beispiel-Defaults.
   */
  preview: async (payload: { subject?: string; body: string; variables?: Record<string, string> }): Promise<string> => {
    const res = await apiV2.post<{ type: string; html: string }>('/admin/email-templates/preview', payload);
    return res.data.html ?? '';
  },

  /**
   * POST /v2/admin/email-templates/{id}/send → Test-/Echtversand via Brevo.
   * Wirft bei 503 (brevo_not_configured) / 502 (brevo_error); die Fehler-
   * Message liegt dann in err.response.data.message.
   */
  send: async (id: number, payload: { to: string; variables?: Record<string, string> }): Promise<EmailTemplateSendResult> => {
    const res = await apiV2.post<{ type: string; message: string; result?: EmailTemplateSendResult }>(
      `/admin/email-templates/${id}/send`,
      payload,
    );
    return res.data.result ?? { ok: true, message: res.data.message };
  },
};

/**
 * Admin-Datei-Upload für E-Mail-Vorlagen-Anhänge.
 * POST /v1/admin/admin-upload (multipart, Feld `files[]`) → AdminUpload.
 * Die zurückgegebene `id` wird in EmailTemplate.attachments[] gespeichert;
 * der Server (collectAttachments) löst sie beim Versand zu Brevo-Attachments auf.
 */
export async function uploadAdminAttachment(file: File): Promise<AdminUploadResult> {
  const fd = new FormData();
  fd.append('files[]', file);
  const res = await apiV1.post<{ type: string; uploads?: AdminUploadResult[]; message?: string }>(
    '/admin/admin-upload',
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  if (res.data.type !== 'success' || !res.data.uploads?.length) {
    throw new Error(res.data.message ?? i18n.t('pflege.detail.dashboard.upload_error'));
  }
  return res.data.uploads[0];
}

/* ─── Systemweite Einstellungen (Admin Einstellungen → Organisation) ────────
 *
 * GET/PUT /v2/admin/system-settings → { data: { timezone, currency } }.
 * `company_name` ist optional Teil des Shapes (Organisation-Sektion);
 * das PUT erfordert serverseitig die Permission `menu.admin_einstellungen`.
 */
export interface SystemSettings {
  company_name?: string | null;
  timezone: string;
  currency: string;
}

export const systemSettingsApi = {
  get: async (): Promise<SystemSettings> => {
    const res = await apiV2.get<{ data: SystemSettings }>('/admin/system-settings');
    return res.data.data;
  },

  update: async (payload: SystemSettings): Promise<SystemSettings> => {
    const res = await apiV2.put<{ data: SystemSettings }>('/admin/system-settings', payload);
    return res.data.data;
  },
};

/* ─── Benachrichtigungs-Präferenzen (Einstellungen → Benachrichtigungen) ────
 *
 * GET/PUT /v2/admin/notification-preferences
 *   → { data: { email: bool, push: bool, sms: bool } }
 */
export interface NotificationPreferences {
  email: boolean;
  push: boolean;
  sms: boolean;
}

export const notificationPreferencesApi = {
  get: async (): Promise<NotificationPreferences> => {
    const res = await apiV2.get<{ data: NotificationPreferences }>('/admin/notification-preferences');
    return res.data.data;
  },

  update: async (payload: NotificationPreferences): Promise<NotificationPreferences> => {
    const res = await apiV2.put<{ data: NotificationPreferences }>('/admin/notification-preferences', payload);
    return res.data.data;
  },
};

// Default export = apiV2 (backward-compat for LanguageSwitcher + existing imports)
export default apiV2;
